import {
  Box,
  Button,
  Checkbox,
  Header,
  Modal,
  SpaceBetween,
  Toggle,
  TopNavigation,
} from '@cloudscape-design/components';
import ScanField from 'features/scan/components/ScanField';
import { ScanTypes } from 'features/scan/utils/parseScanCode';
import { useTheme } from 'features/theme/themeProvider';
import { getAuth, signOut } from 'firebase/auth';
import { useUserData } from 'providers/userDataProvider';
import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import OmniSearch from '../OmniSearch';
import BatchScanTable from './batchScanTable';

function Navigation() {
  const { user } = useUserData();
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const [isBatchScan, setIsBatchScan] = useState(false);
  const [scannedTravelers, setScannedTravelers] = useState([]);
  const [isOpenInTab, setIsOpenInTab] = useState(localStorage.getItem('isOpenScanInTab') === 'true');

  const navigate = useNavigate();
  const { isDarkMode, setIsDarkMode } = useTheme();

  const handleQRCodeSuccess = useCallback(({ type, id }) => {
    if (isBatchScan) {
      setScannedTravelers((prev) => {
        if (prev.includes(id)) {
          return prev;
        }
        return [...prev, id];
      });
    } else {
      const path = type === ScanTypes.LOT ? `/lots/${id}` : `/travelers/${id}`;
      // use localStorage.getItem instead of isOpenInTab to avoid unnecessary re-renders
      const isNewTab = localStorage.getItem('isOpenScanInTab') === 'true';
      if (isNewTab) {
        window.open(path, '_blank', 'noopener, noreferrer');
        setIsScannerOpen(false);
      } else {
        navigate(path);
        setIsScannerOpen(false);
      }
    }
  }, [isBatchScan, navigate]);

  if (!user) {
    return null;
  }

  return (
    <>
      <Modal
        header={(
          <Header
            actions={(
              !isBatchScan && (
                <Toggle
                  onChange={({ detail }) => {
                    localStorage.setItem('isOpenScanInTab', detail.checked);
                    setIsOpenInTab(detail.checked);
                  }}
                  checked={isOpenInTab}
                >
                  Open in new tab
                </Toggle>
              )
            )}
          >
            Scan
          </Header>
        )}
        footer={(
          <Box float="right">
            <Button
              variant="link"
              onClick={() => { setIsScannerOpen(false); }}
            >
              Cancel
            </Button>
          </Box>
        )}
        visible={isScannerOpen}
        onDismiss={() => { setIsScannerOpen(false); setScannedTravelers([]); }}
      >
        <SpaceBetween size="m">
          {isScannerOpen ? (
            <ScanField
              id="nav-scanner"
              // Batch scan collects travelers only; single scans open either page.
              accept={isBatchScan ? [ScanTypes.TRAVELER] : [ScanTypes.TRAVELER, ScanTypes.LOT]}
              onScan={handleQRCodeSuccess}
              cameraMode="inline"
              autoFocus
            />
          ) : null}

          <Checkbox
            onChange={({ detail }) => setIsBatchScan(detail.checked)}
            checked={isBatchScan}
          >
            Batch Scan
          </Checkbox>

          {scannedTravelers.length > 0 && isBatchScan && (
            <BatchScanTable scannedTravelers={scannedTravelers} />
          )}
        </SpaceBetween>
      </Modal>
      <TopNavigation
        identity={{
          onFollow: () => { navigate('/'); },
          title: <span style={{ cursor: 'pointer' }}>Parascope</span>,
          logo: { src: `${process.env.PUBLIC_URL}/badge-white.svg`, alt: 'Parascope logo' },
        }}
        utilities={[
          {
            type: 'button',
            text: 'Scan',
            iconName: 'file-open',
            ariaLabel: 'Scan a traveler or lot',
            disableUtilityCollapse: true,
            onClick: () => { setIsScannerOpen(true); },
          },
          {
            type: 'button',
            text: isDarkMode ? 'Light Mode' : 'Dark Mode',
            disableUtilityCollapse: true,
            onClick: () => setIsDarkMode((prev) => !prev),
          },
          {
            type: 'button',
            variant: 'link',
            iconName: 'gen-ai',
            text: 'Wishlist',
            ariaLabel: 'Parascope Wishlist',
            disableUtilityCollapse: true,
            href: 'https://form.asana.com/?k=L_SKcM8MkFECzw68gUAivw&d=1202767799562169',
            target: '_blank',
          },
          {
            type: 'button',
            text: 'Sign out',
            iconName: 'user-profile',
            ariaLabel: 'Sign out',
            disableUtilityCollapse: true,
            onClick: () => { signOut(getAuth()); },
          },
        ]}
        search={<OmniSearch />}
      />
    </>
  );
}

export default Navigation;
